import React, { useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Icons,
  Menu,
  MenuItem,
  Modal,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import useIsOverflowing from '@app/hooks/useIsOverflowing';
import { createStyles } from './PostCard.styles';
import type { PostCardLayoutProps, PostCardMenuItem } from './types';
import { COMMON_ROOM } from '@core/constants/postFeedType';
import {
  ANNOUNCEMENTS_TEXT,
  CANCEL,
  CLOSE,
  COMMON_ROOM_TEXT,
  DELETE,
} from './text';

interface DeletePostDialogProps {
  feedTypeText: any;
  styles: any;
  open: boolean;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const PostCardLayout = ({
  feedType = COMMON_ROOM,
  author,
  timestamp,
  edited = false,
  content,
  media,
  tags,
  reactions,
  comments,
  postActions,
  ariaLabel = 'Post',
  isPinnedPost = false,
  isRecentlyPinnedPost = false,
  menuItems = [],
  onSelectMenuItem,
  deleteDialogOpen = false,
  onCloseDeleteDialog,
  onConfirmDelete,
  isLoading = false,
}: PostCardLayoutProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [authorNameRef, isAuthorNameOverflowing] =
    useIsOverflowing<HTMLDivElement>();
  const initials =
    author.initials ||
    (author.firstName?.charAt(0).toUpperCase() || 'U') +
      (author.lastName?.charAt(0).toUpperCase() || 'U');

  // Get the text for the post card based on the feed type
  const feedTypeText =
    feedType === COMMON_ROOM ? COMMON_ROOM_TEXT : ANNOUNCEMENTS_TEXT;

  const renderSection = (
    label: string,
    children: React.ReactNode,
    options: { showDivider?: boolean; sectionStyles?: any } = {}
  ) => {
    if (!children) return null;
    const { showDivider = true, sectionStyles } = options;

    return (
      <>
        {showDivider ? <Box component="hr" sx={styles.divider} /> : null}
        <Box
          component="section"
          aria-label={label}
          sx={sectionStyles ? [styles.section, sectionStyles] : styles.section}
        >
          {children}
        </Box>
      </>
    );
  };

  return (
    <Box
      component="article"
      aria-label={ariaLabel}
      sx={
        isRecentlyPinnedPost
          ? [styles.card, styles.recentlyPinnedCard]
          : styles.card
      }
    >
      {feedType === COMMON_ROOM && (isPinnedPost || isRecentlyPinnedPost) && (
        <Alert severity="info" sx={styles.pinnedPostAlert}>
          {isRecentlyPinnedPost
            ? feedTypeText.recentlyPinnedPost
            : feedTypeText.pinnedPost}
        </Alert>
      )}
      <Box component="header" sx={styles.header}>
        <Box sx={styles.author}>
          <Avatar
            altText={author.name}
            fallbackText={initials}
            kind="circular"
            src={author.avatarUrl}
            sx={styles.avatar}
          />
          <Box sx={styles.authorDetails}>
            <Box sx={styles.authorNameRow}>
              <Tooltip
                title={author.name}
                disableFocusListener={!isDesktop || !isAuthorNameOverflowing}
                disableHoverListener={!isDesktop || !isAuthorNameOverflowing}
                disableTouchListener={!isDesktop || !isAuthorNameOverflowing}
              >
                <Box ref={authorNameRef} sx={styles.authorName}>
                  {author.name}
                </Box>
              </Tooltip>
              {author.role ? (
                <Chip label={author.role} size="small" sx={styles.roleBadge} />
              ) : null}
            </Box>
            <Typography sx={styles.timestamp}>{timestamp}</Typography>
          </Box>
        </Box>

        {postActions ||
          (menuItems.length > 0 && onSelectMenuItem ? (
            <ThreeDotMenu
              feedTypeText={feedTypeText}
              styles={styles}
              menuItems={menuItems}
              onSelectMenuItem={onSelectMenuItem}
              isDesktop={isDesktop}
            />
          ) : null)}
      </Box>
      <DeletePostDialog
        feedTypeText={feedTypeText}
        styles={styles}
        open={deleteDialogOpen}
        loading={isLoading}
        onClose={() => onCloseDeleteDialog?.()}
        onConfirm={() => onConfirmDelete?.()}
      />
      {content ? <Box sx={styles.content}>{content}</Box> : null}
      {media ? <Box sx={styles.media}>{media}</Box> : null}
      {edited ? (
        <Typography sx={styles.edited}>{feedTypeText.edited}</Typography>
      ) : null}
      {renderSection(feedTypeText.tagsSection, tags)}
      {renderSection(feedTypeText.reactionsSection, reactions, {
        sectionStyles: styles.reactionsSection,
      })}
      {renderSection(feedTypeText.commentsSection, comments, {
        showDivider: false,
        sectionStyles: styles.commentsSection,
      })}
    </Box>
  );
};

export default PostCardLayout;

const ThreeDotMenu = ({
  feedTypeText,
  styles,
  menuItems,
  onSelectMenuItem,
  isDesktop,
}: {
  feedTypeText: any;
  styles: any;
  menuItems: PostCardMenuItem[];
  onSelectMenuItem: (item: PostCardMenuItem) => void;
  isDesktop: boolean;
}) => {
  const [showOptions, setShowOptions] = useState<HTMLElement | null>(null);

  return (
    <>
      <IconButton
        aria-label={feedTypeText.postOptions}
        size="small"
        sx={styles.options}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          setShowOptions(event.currentTarget);
        }}
      >
        <Tooltip
          disableInteractive
          title="Post options"
          disableFocusListener={!isDesktop}
          disableHoverListener={!isDesktop}
          disableTouchListener={!isDesktop}
        >
          <Icons.MoreVert />
        </Tooltip>
      </IconButton>
      <Menu
        sx={styles.menu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        anchorEl={showOptions}
        open={Boolean(showOptions)}
        onClose={() => {
          setShowOptions(null);
        }}
        onClick={(e) => e.stopPropagation()}
        slotProps={{
          paper: {
            style: {
              width: '160px',
              maxWidth: '160px',
            },
          },
        }}
      >
        {menuItems?.map((item) => (
          <MenuItem
            key={item.key}
            label={item.label}
            onClick={() => {
              setShowOptions(null);
              onSelectMenuItem(item);
            }}
          />
        ))}
      </Menu>
    </>
  );
};

const DeletePostDialog: React.FC<DeletePostDialogProps> = ({
  feedTypeText,
  styles,
  open,
  loading = false,
  onClose,
  onConfirm,
}) => {
  return (
    <Modal open={open} onClose={loading ? undefined : onClose}>
      <Box sx={styles.dialog}>
        <Box sx={styles.dialogHeader}>
          <Typography
            sx={styles.title}
            id="delete-assignment-title"
            tabIndex={0}
          >
            {feedTypeText.deletePostTitle}
          </Typography>
          <IconButton
            aria-label={feedTypeText.closeDeleteDialog}
            onClick={onClose}
            sx={styles.closeButton}
            disabled={loading}
          >
            <Tooltip title={CLOSE}>
              <Icons.Close />
            </Tooltip>
          </IconButton>
        </Box>

        <Typography component="div" sx={styles.body} tabIndex={0}>
          {feedTypeText.deletePostConfirmation}
        </Typography>

        <Box sx={styles.footer}>
          <Button
            kind="secondary"
            type="button"
            sx={styles.buttonSx}
            onClick={onClose}
            disabled={loading}
          >
            {CANCEL}
          </Button>
          <Button
            kind="primary"
            type="button"
            sx={styles.buttonSx}
            onClick={onConfirm}
            disabled={loading}
            loading={loading}
          >
            {DELETE}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
